import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useInteractive, useLanding } from '@/components/Landing/LandingKit';
import { Card } from '@/components/Landing/MarketingKit';
import { EASE, prefersReducedMotion } from '@/components/Landing/landingTokens';
import films from '@/constants/useCaseFilms.json';

/** A single discoverable audience, using the same content as its landing page. */
export default function UseCaseDirectoryCard({ data }) {
  const { c, isXS } = useLanding();
  const { hovered, handlers } = useInteractive();
  const [focused, setFocused] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const poster = films[data.slug]?.poster;
  const showPoster = poster && !imageFailed;
  const lifted = hovered && !prefersReducedMotion();

  return <Link href={`/use-cases/${data.slug}`} asChild>
    <Pressable {...handlers} accessibilityRole="link" accessibilityLabel={`Explore ${data.name}`}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{ width: '100%', height: '100%', minWidth: 0, cursor: 'pointer',
        outlineStyle: focused ? 'solid' : 'none', outlineWidth: 2, outlineColor: c.ink,
        outlineOffset: 5, borderRadius: 20, textDecorationLine: 'none' }}>
      <Card tag="article" padded={false} style={{ flex: 1, minWidth: 0, overflow: 'hidden',
        backgroundColor: c.card, borderStyle: 'solid', borderColor: hovered || focused ? c.ruleStrong : c.rule,
        transform: lifted ? 'translateY(-3px)' : 'translateY(0)',
        transition: `transform 200ms ${EASE}, border-color 200ms ${EASE}` }}>
        <View style={{ padding: isXS ? 24 : 28, paddingBottom: 20, gap: 14 }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <Text tag="h3" weight="semibold" style={{ flex: 1, color: c.ink, fontSize: isXS ? 23 : 25,
              lineHeight: isXS ? 29 : 32, letterSpacing: -0.65 }}>{data.name}</Text>
            <View aria-hidden style={{ paddingTop: 5 }}><Icon name="arrow-up-right" size={16} color={c.ink} /></View>
          </View>
          {!!data.subtitle && <Text tag="p" numberOfLines={4} style={{ color: c.muted, fontSize: 15,
            lineHeight: 24, marginBottom: 0 }}>{data.subtitle}</Text>}
        </View>
        <View style={{ flex: 1 }} />
        <View aria-hidden style={{ marginHorizontal: 16, padding: 14, borderRadius: 12,
          backgroundColor: c.panel, backgroundImage: 'var(--preview-gradient)', aspectRatio: 16 / 10,
          overflow: 'hidden', justifyContent: 'center' }}>
          {showPoster ? <img src={poster} alt="" loading="lazy" decoding="async" width="1280" height="720"
            onError={() => setImageFailed(true)} style={{ display: 'block', width: '100%', height: '100%',
              objectFit: 'cover', borderRadius: 8 }} />
            : <View style={{ backgroundColor: c.cardSolid, borderRadius: 8, padding: 22, flex: 1,
                justifyContent: 'center', gap: 14 }}>
              <Icon name={data.icon || 'sparkles'} size={24} color={c.ink} />
              <Text weight="semibold" style={{ color: c.ink, fontSize: 22, lineHeight: 28,
                letterSpacing: -0.6 }}>{data.headline || data.name}</Text>
            </View>}
        </View>
        <View style={{ paddingHorizontal: isXS ? 24 : 28, paddingVertical: 22,
          flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text tag="span" weight="medium" style={{ color: c.ink, fontSize: 13 }}>Explore the use case</Text>
          <View aria-hidden><Icon name="arrow-right" size={12} color={c.ink} /></View>
        </View>
      </Card>
    </Pressable>
  </Link>;
}
